import UploadDialog from "components/UploadDialog";
import { createAlertAction } from "data/app/appActions";
import api from "lib/api";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { createAlert } from "records/Alert";
import store from "store";
import { parseString } from "xml2js";

interface Props {
	onNewWorkflowAdded: () => void;
}

export default function UploadWorkflow({ onNewWorkflowAdded }: Props) {
	const { t } = useTranslation();

	const [isUploaded, setIsUploaded] = useState<boolean>(false);

	const [error, setError] = useState<string | null>(null);

	/**
	 * Handles uploading the workflow by first changing the file to FormData type
	 * then tries uploading the form
	 *
	 * @param file uploaded to the UploadDialog
	 */
	const handleUpload = useCallback(
		async file => {
			// setError(null);
			try {
				// Change file into FormData type
				const formData = new FormData();
				const blob = new Blob([file], { type: "text/xml" });
				formData.append("file", blob);

				// When the file has been converted into the FormData type
				// we can try uploading the workflow
				await api.workflow.uploadWorkflowDefinition(formData);

				// Set isUploaded as true so that the UploadDialog knows
				// so that we can close the upload modal
				setIsUploaded(true);

				onNewWorkflowAdded();

				// Display success alert that the workflow has been successfully uploaded
				store.dispatch(
					createAlertAction(
						createAlert({
							title: t("alerts.workflowAdded.title"),
							status: "success",
							timeout: 3000,
						})
					)
				);
			} catch (error) {
				console.error(error);
				// Show message that workflow has failed to import if api call fails
				setError("workflowImportFail");
			}
		},
		[onNewWorkflowAdded, t]
	);

	const handleValidation = useCallback(
		async file => {
			setIsUploaded(false);
			// setError(null);

			let id;

			try {
				// Parse the XML so that we can grab the id
				parseString(
					file,
					// @ts-expect-error 123
					(
						err: Error,
						result: {
							definitions: { process: [{ $: { id: string } }] };
						}
					) => {
						id = result.definitions?.process[0].$?.id;
						console.error(err);
					}
				);
			} catch (error) {
				console.error(error);
				// If id is undefined the workflow is invalid and will jump to this catch block
				setError("invalidFile");
			}

			if (id) {
				try {
					// Check if the id exists in current workflows
					const { results } =
						await api.workflow.getAllWorkflowDefinitions({
							limit: 20,
							offset: 0,
							key: id,
						});

					// If the id exists then we need to display the warning dialog
					if (results.length) {
						setError("workflowExists");
					} else {
						// If it doesn't exist we can try uploading the form
						handleUpload(file);
					}
				} catch (error) {
					console.error(error);
					// Show message that workflow has failed to import if api call fails
					setError("workflowImportFail");
				}
			}
		},
		[handleUpload]
	);

	return (
		<UploadDialog
			btnLabel={t("main.workflows.labels.importWorkflow")}
			title={t("main.workflows.labels.importWorkflow")}
			acceptXml
			onValidation={handleValidation}
			file="workflow"
			btnClass="mb-3 mb-md-0 text-nowrap"
			isUploaded={isUploaded}
			error={error}
			clearError={() => setError(null)}
		/>
	);
}
